import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from '@mui/material'
import { toast } from 'react-toastify'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import documentAccessAPI, { announceChange, DOCUMENT_ACCESS_CHANGED } from 'features/documentAccess/documentAccessAPI'
import RequestAccessDialog from 'features/documentAccess/RequestAccessDialog'
import { LOCKED_EXPLAINER, SUCCESSION_EXPLAINER, errorMessage, formatDate, statusMeta } from 'features/documentAccess/accessCopy'
import { ACCENT, ACCENT_SOFT, LINK, MUTED } from 'features/actions/workspace/ui'

const stop = e => e.stopPropagation()

const LockedFileAccess = ({ file, bpId, compact = false }) => {
  const [request, setRequest] = useState(file?.accessRequest || null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => { setRequest(file?.accessRequest || null) }, [file?.accessRequest])

  useEffect(() => {
    const fileId = file?.id
    const onChange = e => {
      const hit = (e?.detail?.requested || []).find(r => r.fileId === fileId)
      if (hit) setRequest({ id: hit.requestId, status: 'pending' })
    }
    window.addEventListener(DOCUMENT_ACCESS_CHANGED, onChange)
    return () => window.removeEventListener(DOCUMENT_ACCESS_CHANGED, onChange)
  }, [file?.id])

  if (!file || file.canBeAccessed !== false || file.isIdentityRestricted || !bpId) return null

  const explainer = `${LOCKED_EXPLAINER}${file.accessLockedBySuccession ? ` ${SUCCESSION_EXPLAINER}` : ''}`
  const status = request?.status
  const blockedUntil = status === 'refused' ? request?.canAskAgainAt : null

  const withdraw = async () => {
    setBusy(true)
    try {
      await documentAccessAPI.withdraw(request.id)
      setRequest({ ...request, status: 'withdrawn' })
      toast.success('Request withdrawn.')
      announceChange()
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Wrap data-locked-access='' onClick={stop} onMouseDown={stop} onTouchStart={stop} $compact={compact}>
      {status === 'pending' && (
        <>
          <Tooltip title={statusMeta('pending').help} placement='top'>
            <Chip style={{ color: statusMeta('pending').rag.fg, background: statusMeta('pending').rag.bg }}>Access requested</Chip>
          </Tooltip>
          <Quiet type='button' disabled={busy} onClick={withdraw}>Withdraw</Quiet>
        </>
      )}

      {status === 'refused' && blockedUntil && (
        <Tooltip title={request.decisionNote ? `Their note: ${request.decisionNote}` : statusMeta('refused').help} placement='top'>
          <Chip style={{ color: statusMeta('refused').rag.fg, background: statusMeta('refused').rag.bg }}>
            Refused · ask again from {formatDate(blockedUntil)}
          </Chip>
        </Tooltip>
      )}

      {(status !== 'pending' && !blockedUntil) && (
        <Tooltip title={explainer} placement='top' arrow describeChild>
          <AskButton type='button' $compact={compact} onClick={() => setOpen(true)}>
            <LockOutlinedIcon aria-hidden='true' style={{ fontSize: 16 }} />
            Request access
          </AskButton>
        </Tooltip>
      )}

      <RequestAccessDialog
        open={open}
        bpId={bpId}
        files={[{ id: file.id, name: file.displayName || file.name }]}
        onClose={() => setOpen(false)}
        onDone={results => {
          const created = results.find(r => r.status === 'created')
          if (created) setRequest({ id: created.requestId, status: 'pending' })
        }}
      />
    </Wrap>
  )
}

export default LockedFileAccess

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  padding: ${({ $compact }) => ($compact ? '0' : '0 12px 12px')};
  cursor: default;
  ${({ $compact }) => $compact && `
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    z-index: 1;
  `}

  @media (max-width: 768px) {
    position: static;
    transform: none;
    padding: ${({ $compact }) => ($compact ? '6px 8px 0 4px' : '0 12px 12px')};
  }
`

const Chip = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
`

const AskButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: ${({ $compact }) => ($compact ? '0 0 auto' : '1 1 100%')};
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${ACCENT};
  border-radius: 8px;
  background: #ffffff;
  color: ${LINK};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  &:hover { background: ${ACCENT_SOFT}; }
  &:focus-visible { outline: 2px solid ${LINK}; outline-offset: 2px; }
`

const Quiet = styled.button`
  min-height: 40px;
  padding: 0 6px;
  border: none;
  background: none;
  color: ${MUTED};
  font: inherit;
  font-size: 14px;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
`
